/**
 * Diagnostic des pastilles (Paramètres) — pour chaque source : compteur actuel, où agir,
 * comment la pastille disparaît. Bouton de test (fausse question) + nettoyage.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { ArrowLeft, Wrench, RefreshCw, Bell, FlaskConical, Trash2, Loader2, ExternalLink } from "lucide-react";

export default function BadgesDiagnosticPage() {
  const navigate = useNavigate();
  const [diag, setDiag] = useState({ sources: [], total_badges: 0 });
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [cleaningTests, setCleaningTests] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/badges/diagnostic");
      setDiag(r.data || { sources: [], total_badges: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshBadges = () => window.dispatchEvent(new CustomEvent("badges:refresh"));

  const handleTestPing = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/badges/test-ping", {});
      toast.success(r.data.message || "Faux signal créé", { duration: 8000 });
      refreshBadges();
      setTimeout(load, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setTesting(false); }
  };

  const handleCleanupTests = async () => {
    setCleaningTests(true);
    try {
      const r = await api.post("/admin/badges/cleanup-test-pings", {});
      toast.success(`${r.data.deleted} faux signaux supprimés`);
      refreshBadges();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setCleaningTests(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-2" data-testid="badges-diagnostic-page">
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate("/admin/settings")}
          className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
          data-testid="badges-diag-back"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
          <Wrench className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Diagnostic des pastilles</h1>
          <p className="text-sm text-gray-500">Une pastille = une intervention à faire. Elle disparaît quand l'action est faite.</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 transition" title="Recharger" data-testid="badges-diag-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="mb-5 p-4 rounded-xl bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200 flex items-center gap-4">
        <Bell className="w-8 h-8 text-rose-700" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-rose-700 font-semibold">Interventions en attente</div>
          <div className="text-3xl font-bold text-rose-900" data-testid="badges-total">{diag.total_badges}</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleTestPing}
            disabled={testing}
            className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="badges-diag-test-btn"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
            Tester une pastille
          </button>
          <button
            onClick={handleCleanupTests}
            disabled={cleaningTests}
            className="px-3 py-2 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="badges-diag-cleanup-btn"
          >
            {cleaningTests ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Nettoyer tests
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
        <div className="px-4 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-700">État par source</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-right">Pastille</th>
                <th className="px-3 py-2 text-left">Où agir</th>
                <th className="px-3 py-2 text-left">Comment elle disparaît</th>
              </tr>
            </thead>
            <tbody>
              {(diag.sources || []).map((s) => (
                <tr key={s.key} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`badges-diag-row-${s.key}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{s.source}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={`tabular-nums font-semibold ${s.badge_count > 0 ? "text-rose-700" : "text-emerald-600"}`} data-testid={`badges-diag-count-${s.key}`}>
                      {s.badge_count}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => navigate(s.path)}
                      className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
                      data-testid={`badges-diag-go-${s.key}`}
                    >
                      {s.path} <ExternalLink className="w-3 h-3" />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">{s.how}</td>
                </tr>
              ))}
              {loading && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
        La cloche (en haut) est un simple fil d'information ; les pastilles rouges du menu et des tuiles ne comptent que les interventions.
        Données démo, archivées ou à la corbeille : jamais comptées.
      </p>
    </div>
  );
}
